import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { AuthenticationService, AppConfigService } from '@alfresco/adf-core';
import { Subject, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

/** A picked directory entry — user or role — kept structured so the id survives for routing. */
export interface DirectoryRef {
  id: string;
  displayName: string;
  email?: string;
  kind: 'USER' | 'ROLE';
  /** When a ROLE is resolved to a specific person (footerResolve): the picked member.
   *  Routing still uses {@link id} (the role); this is only for the document/footer. */
  footerPerson?: DirectoryRef;
  /** The picked person's HR designation — printed in the memo for To/Through/From
   *  (in place of the role name). Present on a resolved person or its role entry. */
  designation?: string;
  /** HR org context of the picked person (from wf_user_directory, returned by the user-search API).
   *  Lets a text/aspect field capture the person's department/division — not just their name. */
  sectionType?: string;
  directorateName?: string;
  departmentName?: string;
  divisionName?: string;
  /** For a ROLE/office: the DEFAULT head's designation (office title). Preferred over the picked
   *  member's own designation, so an acting/alternate signatory still prints the office title. */
  officeDesignation?: string;
}

/**
 * Reusable Keycloak-backed person / role picker. Searches users and/or roles via the mail-workflow
 * directory endpoints and emits structured {@link DirectoryRef}(s) so the caller keeps the id (for
 * approval routing / distribution) AND the display name (for the document). Supports single or
 * multi (chips). Used by the memo wizard (To/Through/From) and the mail wizard.
 *
 * <p>When {@code footerResolve} is on, a picked ROLE is resolved to its member(s): exactly one →
 * auto-selected; more than one → the user is prompted to pick the individual. The chosen person is
 * carried on the emitted ref as {@code footerPerson} and their {@code designation} is captured for
 * the document. Works for single-select (To/From) AND multi-select (Through) — each role in a
 * multi field resolves its own person.
 */
@Component({
  selector: 'sp-directory-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="dp-wrap">
      <!-- selected chips -->
      <div class="dp-chips" *ngIf="asArray.length">
        <span class="dp-chip" *ngFor="let r of asArray">
          <span class="material-icons">{{ r.kind === 'ROLE' ? 'groups' : 'person' }}</span>
          {{ r.displayName }}
          <span class="dp-chip-who" *ngIf="r.footerPerson"> · {{ r.footerPerson.displayName }}</span>
          <button type="button" class="dp-chip-x" (click)="remove(r)" aria-label="Remove">×</button>
        </span>
      </div>

      <!-- search box (hidden for single once chosen) -->
      <div class="dp-search" *ngIf="multi || asArray.length === 0">
        <span class="material-icons dp-search-ic">search</span>
        <input type="text" [placeholder]="placeholder"
               [(ngModel)]="term" (ngModelChange)="onType($event)"
               (focus)="onFocus()" />
        <span class="material-icons spin dp-load" *ngIf="loading">progress_activity</span>
      </div>

      <!-- results dropdown -->
      <div class="dp-menu" *ngIf="open && results.length">
        <button type="button" class="dp-opt" *ngFor="let r of results" (click)="pick(r)">
          <span class="material-icons">{{ r.kind === 'ROLE' ? 'groups' : 'person' }}</span>
          <span class="dp-opt-main">{{ r.displayName }}</span>
          <span class="dp-opt-sub" *ngIf="r.email">{{ r.email }}</span>
          <span class="dp-opt-tag" *ngIf="r.kind === 'ROLE'">role</span>
        </button>
      </div>
      <div class="dp-empty" *ngIf="open && !loading && term.length >= 2 && !results.length">No matches for "{{ term }}"</div>

      <!-- role → member resolution -->
      <div class="dp-resolving" *ngIf="resolving">
        <span class="material-icons spin">progress_activity</span> Resolving role members…
      </div>

      <!-- role has 2+ heads but none flagged default (default-head-only sites) -->
      <div class="dp-fc dp-fc-notice" *ngIf="memberNotice">
        <div class="dp-fc-head">
          <span class="material-icons">info</span>
          <b>{{ memberNotice.role.displayName }}</b> — {{ memberNotice.message }}
          <button type="button" class="dp-fc-dismiss" (click)="memberNotice = null" aria-label="Dismiss">×</button>
        </div>
      </div>

      <!-- one prompt per role with >1 people awaiting a pick -->
      <div class="dp-fc" *ngFor="let c of memberChoices">
        <div class="dp-fc-head">
          <span class="material-icons">warning</span>
          <b>{{ c.role.displayName }}</b> has <b>{{ c.members.length }}</b> people — select the individual:
        </div>
        <div class="dp-fc-opts">
          <button type="button" class="dp-fc-opt" *ngFor="let m of c.members" (click)="chooseMember(c.role, m)">
            {{ m.displayName }}<span class="dp-fc-desg" *ngIf="m.designation"> — {{ m.designation }}</span>
          </button>
        </div>
      </div>

      <!-- resolved people (designation captured for the document) -->
      <div class="dp-fc-set" *ngFor="let r of resolvedList">
        <span class="material-icons">edit_note</span>
        <b>{{ r.footerPerson?.displayName }}</b>
        <span class="dp-fc-desg" *ngIf="r.designation"> — {{ r.designation }}</span>
        <span class="dp-fc-role"> ({{ r.displayName }})</span>
      </div>
    </div>
  `,
  styles: [`
    .dp-wrap { position: relative; }
    .dp-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 6px; }
    .dp-chip { display: inline-flex; align-items: center; gap: 5px; background: #eef2ff; color: #3730a3; border-radius: 999px; padding: 3px 8px 3px 8px; font-size: 12.5px; }
    .dp-chip .material-icons { font-size: 15px; }
    .dp-chip-who { color: #4338ca; font-weight: 600; }
    .dp-chip-x { border: none; background: none; cursor: pointer; color: #6366f1; font-size: 16px; line-height: 1; padding: 0 0 0 2px; }
    .dp-chip-x:hover { color: #312e81; }
    .dp-search { display: flex; align-items: center; gap: 6px; border: 1px solid #d1d5db; border-radius: 6px; padding: 6px 9px; background: #fff; }
    .dp-search-ic { color: #9ca3af; font-size: 18px; }
    .dp-search input { border: none; outline: none; flex: 1; font-size: 13px; background: transparent; }
    .dp-load { font-size: 16px; color: #6366f1; }
    .dp-menu { position: absolute; z-index: 40; left: 0; right: 0; margin-top: 4px; background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 8px 28px rgba(0,0,0,.14); max-height: 260px; overflow-y: auto; }
    .dp-opt { display: flex; align-items: center; gap: 8px; width: 100%; text-align: left; border: none; background: none; padding: 8px 11px; cursor: pointer; font-size: 13px; }
    .dp-opt:hover { background: #f5f7ff; }
    .dp-opt .material-icons { font-size: 18px; color: #6366f1; }
    .dp-opt-main { font-weight: 500; color: #111827; }
    .dp-opt-sub { color: #9ca3af; font-size: 11.5px; margin-left: 4px; }
    .dp-opt-tag { margin-left: auto; font-size: 10px; font-weight: 700; color: #4338ca; background: #e0e7ff; border-radius: 999px; padding: 1px 6px; }
    .dp-empty { font-size: 12px; color: #9ca3af; padding: 6px 4px; }
    .dp-resolving { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #6b7280; margin-top: 6px; }
    .dp-resolving .material-icons { font-size: 16px; }
    /* >1 member in the role → highlighted prompt to choose the individual */
    .dp-fc { margin-top: 8px; border: 1px solid #fca5a5; background: #fef2f2; border-radius: 8px; padding: 9px 11px; }
    .dp-fc-notice { border-color: #93c5fd; background: #eff6ff; }
    .dp-fc-notice .dp-fc-head { color: #1d4ed8; }
    .dp-fc-dismiss { margin-left: auto; border: none; background: transparent; color: inherit; cursor: pointer; font-size: 15px; line-height: 1; opacity: 0.7; }
    .dp-fc-dismiss:hover { opacity: 1; }
    .dp-fc-head { display: flex; align-items: center; gap: 6px; font-size: 12.5px; color: #b91c1c; font-weight: 600; }
    .dp-fc-head .material-icons { font-size: 17px; }
    .dp-fc-opts { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
    .dp-fc-opt { border: 1px solid #fca5a5; background: #fff; color: #991b1b; border-radius: 999px; padding: 4px 12px; font-size: 12.5px; cursor: pointer; }
    .dp-fc-opt:hover { background: #fee2e2; }
    .dp-fc-desg { color: #6b7280; font-weight: 500; }
    .dp-fc-set { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #166534; margin-top: 6px; }
    .dp-fc-set .material-icons { font-size: 16px; }
    .dp-fc-role { color: #6b7280; }
    .spin { animation: dp-spin 1s linear infinite; } @keyframes dp-spin { to { transform: rotate(360deg); } }
  `]
})
export class SpDirectoryPickerComponent implements OnInit {
  /** 'person' = users only, 'role' = roles only, 'both' = users + roles. */
  @Input() mode: 'person' | 'role' | 'both' = 'person';
  @Input() multi = false;
  @Input() placeholder = 'Search users…';
  @Input() value: DirectoryRef | DirectoryRef[] | null = null;
  /** When true: a picked ROLE is resolved to its member(s) — one → auto-selected; more than one →
   *  prompt which individual. The chosen person + their designation ride on the emitted ref. Used
   *  for To/Through/From so the document shows the person's designation (routing stays by role). */
  @Input() footerResolve = false;
  /** Fixed realm-role allow-list (comma-separated) — when set, the USER search is limited to members
   *  of these role(s) (union, de-duplicated). Blank = search everyone. Does not affect role search. */
  @Input() restrictRoles = '';
  @Output() valueChange = new EventEmitter<DirectoryRef | DirectoryRef[] | null>();

  term = '';
  results: DirectoryRef[] = [];
  loading = false;
  open = false;

  // role → member resolution (footerResolve)
  memberChoices: { role: DirectoryRef; members: DirectoryRef[] }[] = [];
  /** Notice shown when a role has 2+ heads but none is flagged default (default-head-only sites). */
  memberNotice: { role: DirectoryRef; message: string } | null = null;
  resolving = false;
  private search$ = new Subject<string>();
  private base: string;

  constructor(private http: HttpClient, private auth: AuthenticationService, appConfig: AppConfigService) {
    this.base = `${appConfig.get<string>('skyplus.apiUrl', '/bpm/api')}/mail/workflow`;
  }

  ngOnInit(): void {
    this.search$.pipe(debounceTime(350), distinctUntilChanged()).subscribe(t => this.run(t));
  }

  get asArray(): DirectoryRef[] {
    if (!this.value) { return []; }
    return Array.isArray(this.value) ? this.value : [this.value];
  }

  /** Roles already resolved to a person — shown as a "designation captured" summary. */
  get resolvedList(): DirectoryRef[] {
    return this.asArray.filter(r => !!r.footerPerson);
  }

  /** Role-restricted user fields may search with an empty term (lists the pool); otherwise ≥2 chars. */
  private get minTermLen(): number {
    return (this.restrictRoles || '').trim() && this.mode !== 'role' ? 0 : 2;
  }

  onType(t: string): void {
    this.term = t;
    if ((t || '').trim().length < this.minTermLen) { this.results = []; this.open = false; return; }
    this.loading = true; this.open = true;
    this.search$.next(t.trim());
  }

  /** Focus: reopen prior results, or — for a role-restricted field with nothing typed — list the pool. */
  onFocus(): void {
    if (this.results.length > 0) { this.open = true; return; }
    if (this.minTermLen === 0 && !(this.term || '').trim()) { this.onType(''); }
  }

  private run(term: string): void {
    const calls: Promise<DirectoryRef[]>[] = [];
    if (this.mode === 'person' || this.mode === 'both') { calls.push(this.fetchUsers(term)); }
    if (this.mode === 'role' || this.mode === 'both') { calls.push(this.fetchRoles(term)); }
    Promise.all(calls).then(lists => {
      const chosen = new Set(this.asArray.map(r => r.kind + ':' + r.id));
      this.results = lists.flat().filter(r => !chosen.has(r.kind + ':' + r.id));
      this.loading = false;
    }).catch(() => { this.results = []; this.loading = false; });
  }

  /** Map a backend user object → DirectoryRef, keeping the HR designation when present. */
  private toRef(u: any): DirectoryRef {
    return { id: u.id, displayName: u.displayName || u.id, email: u.email, designation: u.designation,
             sectionType: u.sectionType, directorateName: u.directorateName,
             departmentName: u.departmentName, divisionName: u.divisionName, kind: 'USER' as const };
  }

  private fetchUsers(term: string): Promise<DirectoryRef[]> {
    let params = new HttpParams().set('filter', term).set('maxItems', '15');
    const roles = (this.restrictRoles || '').trim();
    if (roles) { params = params.set('roles', roles); }   // limit the user search to these roles' members
    return firstValueFrom(this.http.get<any>(`${this.base}/users/search`, { headers: this.h(), params }))
      .then(r => (r?.users || []).map((u: any) => this.toRef(u)))
      .catch(() => [] as DirectoryRef[]);
  }

  private fetchRoles(term: string): Promise<DirectoryRef[]> {
    const params = new HttpParams().set('filter', term).set('maxItems', '15');
    return firstValueFrom(this.http.get<any>(`${this.base}/roles/search`, { headers: this.h(), params }))
      .then(r => (r?.roles || []).map((g: any) => ({ id: g.id || g.name, displayName: g.name || g.displayName || g.id, kind: 'ROLE' as const, officeDesignation: g.officeDesignation })))
      .catch(() => [] as DirectoryRef[]);
  }

  pick(r: DirectoryRef): void {
    this.term = ''; this.results = []; this.open = false;
    if (this.footerResolve && r.kind === 'ROLE') { this.resolveMembers(r); return; }
    this.addValue(r);
  }

  /** Add/replace an entry in the value (dedup by kind+id) and emit. Single = the entry; multi = the list. */
  private addValue(entry: DirectoryRef): void {
    if (this.multi) {
      const arr = this.asArray.filter(x => !(x.kind === entry.kind && x.id === entry.id));
      arr.push(entry);
      this.value = arr; this.valueChange.emit(arr);
    } else {
      this.value = entry; this.valueChange.emit(entry);
    }
  }

  /** Resolve a role's members: 1 → auto-pick; >1 → prompt; 0 → keep the role name only. */
  private resolveMembers(role: DirectoryRef): void {
    this.memberChoices = this.memberChoices.filter(c => c.role.id !== role.id);
    this.memberNotice = null;
    this.resolving = true;
    const params = new HttpParams().set('filter', '').set('role', role.id).set('maxItems', '50');
    firstValueFrom(this.http.get<any>(`${this.base}/users/search`, { headers: this.h(), params }))
      .then(res => {
        this.resolving = false;
        const raw: any[] = res?.users || [];
        // Default-head-only sites (per-site backend flag) may return ONE non-selectable placeholder
        // when a role has 2+ heads but none is flagged default — surface the message, don't commit it.
        if (raw.length === 1 && (raw[0].placeholder || raw[0].selectable === false || !raw[0].id)) {
          this.addValue({ ...role });   // keep the role name only
          this.memberNotice = { role, message: raw[0].displayName || 'No head defined as default for this role' };
          return;
        }
        const members: DirectoryRef[] = raw.map((u: any) => this.toRef(u));
        if (members.length === 1) { this.commit(role, members[0]); }   // 1 head (incl. the default) → auto-pick
        else if (members.length > 1) { this.addValue({ ...role }); this.memberChoices = [...this.memberChoices, { role, members }]; }
        else { this.addValue({ ...role }); }   // no members → role name only
      })
      .catch(() => { this.resolving = false; this.addValue({ ...role }); });
  }

  /** Attach the picked person (+ their designation) to the role entry and emit. */
  private commit(role: DirectoryRef, person: DirectoryRef): void {
    this.addValue({ ...role, footerPerson: person, designation: role.officeDesignation || person.designation });
  }

  chooseMember(role: DirectoryRef, m: DirectoryRef): void {
    this.memberChoices = this.memberChoices.filter(c => c.role.id !== role.id);
    this.commit(role, m);
  }

  remove(r: DirectoryRef): void {
    this.memberChoices = this.memberChoices.filter(c => c.role.id !== r.id);
    if (this.multi) {
      const arr = this.asArray.filter(x => !(x.kind === r.kind && x.id === r.id));
      this.value = arr; this.valueChange.emit(arr);
    } else {
      this.value = null; this.valueChange.emit(null);
    }
  }

  private h(): HttpHeaders {
    const token = this.auth.getToken();
    const base: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) { base['Authorization'] = `Bearer ${token}`; }
    return new HttpHeaders(base);
  }
}
